import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import type { ClientInfo } from "@/context/client-types";
import { clubAccessMessage, normalizeClubPhone, type ClubAccessMode } from '@/lib/clubIdentity';

export async function accessClubClient(client: ClientInfo, mode: ClubAccessMode): Promise<ClientInfo> {
  if (!isSupabaseConfigured || !supabase) throw new Error('El acceso no está disponible. Intenta más tarde.');
  const phone = normalizeClubPhone(client.phone);
  const { data, error } = await supabase.rpc('alea_club_access', {
    access_mode: mode,
    client_name: client.name.trim(),
    client_phone: phone,
    client_email: mode === 'register' ? client.email.trim() : '',
  });
  if (error) throw new Error('No pudimos conectar con ALÉA. Revisa tu conexión e intenta de nuevo.');
  if (!data || !['created', 'existing'].includes(data.status) || typeof data.clientId !== 'string') {
    throw new Error(clubAccessMessage(data?.status));
  }
  // Keep only supplied data; never retrieve stored email or private history.
  return { ...client, name: client.name.trim(), phone, id: data.clientId };
}

export async function createClubAppointment(input: {
  client: ClientInfo | null;
  serviceName: string;
  date: string;
  slot: string;
}) {
  if (!isSupabaseConfigured || !supabase || !input.client) throw new Error('No pudimos conectar para guardar tu cita. Intenta de nuevo.');

  const { error } = await supabase.from("appointments").insert({
    ...(input.client.id ? { client_id: input.client.id } : {}),
    cliente: input.client.name,
    telefono: input.client.phone,
    correo: input.client.email,
    servicio: input.serviceName,
    tipo: "servicio",
    fecha: input.date,
    horario: input.slot,
    estado: "pendiente",
    source: "club_app",
  });

  if (error) {
    if (error.message?.includes('ALEA_SLOT_UNAVAILABLE')) throw new Error('Este horario acaba de ocuparse o bloquearse. Elige otro espacio.');
    if (error.message?.includes('ALEA_INVALID_SLOT')) throw new Error('Este horario no está disponible para reservar.');
    throw new Error('No pudimos guardar tu cita. Revisa tu conexión e intenta de nuevo.');
  }
}

export async function createClubPromoRequest(input: {
  client: ClientInfo | null;
  promoId: string;
  promoName: string;
}) {
  if (!isSupabaseConfigured || !supabase) return;

  const { error } = await supabase.from("promo_requests").insert({
    ...(input.client?.id ? { client_id: input.client.id } : {}),
    promo_id: input.promoId,
    promo_name: input.promoName,
    cliente: input.client?.name ?? null,
    telefono: input.client?.phone ?? null,
    correo: input.client?.email ?? null,
    status: "solicitada",
  });

  if (error) {
    console.warn("No se pudo guardar la promocion en Supabase", error);
  }
}
